/**
 * @module HypothesisScopeFunnel
 * @purpose Renders the per-artefact-type signal-count grid, with an event-logs placeholder
 *   shown only when signalCounts has no eventlog key.
 * @depends types/reasoning (SignalCounts), react-i18next
 * @dependedOnBy HypothesisCard
 *
 * @rules
 * - Performance: pure display — props only, no state
 * - State: none
 * - Errors: n/a — purely presentational; empty signalCounts renders only the event-logs placeholder
 */

import { useTranslation } from 'react-i18next'
import type { SignalCounts } from '../../../../../types/reasoning'

interface HypothesisScopeFunnelProps {
  signalCounts: SignalCounts
  totalObservations: number
}

const CONTAINER_STYLE: React.CSSProperties = {
  borderTop: '1px solid var(--border)',
  padding: '10px 0',
}

const SECTION_HEADING: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  marginBottom: '6px',
}

const COUNT_GRID: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
}

const COUNT_CELL: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: '4px',
  padding: '4px 8px',
  background: 'transparent',
}

const ARTEFACT_TYPE_LABEL: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}

const COUNT_VALUE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.7rem',
  color: 'var(--text)',
  marginTop: '2px',
}

const DEFERRED_CELL: React.CSSProperties = {
  ...COUNT_CELL,
  borderStyle: 'dashed',
}

const DEFERRED_LABEL: React.CSSProperties = {
  ...ARTEFACT_TYPE_LABEL,
  fontStyle: 'italic',
}

/**
 * Renders the signal-scope grid — one cell per artefact type, plus an event-logs
 * placeholder cell when signalCounts has no eventlog key.
 *
 * @behaviour Each cell shows the artefact type name and its count in "X of Y total" form.
 *   The event-logs placeholder cell renders only when signalCounts lacks an "eventlog"
 *   key — signalCounts is a SQL GROUP BY result, so an artefact type with zero
 *   observations has no key at all (0 is a measurement, not a failure).
 * @errors n/a
 */
export function HypothesisScopeFunnel({ signalCounts, totalObservations }: HypothesisScopeFunnelProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const entries = Object.entries(signalCounts)

  return (
    <div style={CONTAINER_STYLE}>
      <div style={SECTION_HEADING}>{t('reasoning.hypothesis.scopeFunnel.title')}</div>
      <div style={COUNT_GRID}>
        {entries.map(([artefactType, count]) => (
          <div key={artefactType} style={COUNT_CELL}>
            <div style={ARTEFACT_TYPE_LABEL}>{t(`canvas.artefactTypes.${artefactType}` as never, { defaultValue: artefactType })}</div>
            <div style={COUNT_VALUE}>
              {t('reasoning.hypothesis.scopeFunnel.ofTotal', { count, total: totalObservations })}
            </div>
          </div>
        ))}
        {signalCounts.eventlog === undefined && (
          <div style={DEFERRED_CELL}>
            <div style={DEFERRED_LABEL}>{t('reasoning.hypothesis.scopeFunnel.eventLogsNoObservations')}</div>
          </div>
        )}
      </div>
    </div>
  )
}
